/**
 * Recursively replaces strings in an object based on provided one or more search values.
 */
export function deepStringReplace<T>(
  obj: T,
  replacers: Array<{ searchValue: RegExp | string; replaceValue: string }>,
  maxDepth = 10,
): T {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (typeof obj === "string") {
    let replacedString: string = obj;
    for (const replacer of replacers) {
      if (replacer.searchValue instanceof RegExp) {
        // Ensure global flag is present so that we replace all occurrences of the search value
        // Otherwise replaceAll will throw an error
        if (!replacer.searchValue.flags.includes("g")) {
          throw new Error(
            "You will need to use the global flag when using a regex. Add a `g` at the end and you are good to go.",
          );
        }
      }
      replacedString = replacedString.replaceAll(
        replacer.searchValue,
        replacer.replaceValue,
      );
    }
    return replacedString as unknown as T;
  }

  if (maxDepth <= 0) {
    return "[max depth exceeded]" as unknown as T;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) =>
      deepStringReplace(item, replacers, maxDepth - 1)
    ) as unknown as T;
  }

  if (typeof obj === "object") {
    // Preserve prototype chain when creating new object so we preserve for an example class methods, instanceof checks etc.
    const replacedObject = Object.create(Object.getPrototypeOf(obj));
    // Loop through both own property names (both enumerable and non-enumerable)
    for (const key of Object.getOwnPropertyNames(obj)) {
      replacedObject[key] = deepStringReplace(
        (obj as any)[key],
        replacers,
        maxDepth - 1,
      );
    }
    return replacedObject as T;
  }

  return obj;
}
